import React, { useEffect } from 'react'
import { MenuItem, Intent } from '@blueprintjs/core'
import { Button } from '@ui'
import { Select2 } from '@blueprintjs/select'
import { colors } from './colors'
import SimpleColorPicker from './SimpleColorPicker'
import { QrCodeFileFormat } from '@api/QRCode/download'
import css from './qrContent.module.scss'

interface PrintOptions {
  title: string
  value: string
}

const PrintOptionsComponent = Select2<PrintOptions>

const options = [
  { value: 'LETTER:50', title: 'US Letter - Size: 1.5" x 1", 50 labels per sheet' },
  { value: 'LETTER:18', title: 'US Letter - Size: 2.5" x 1.563", 18 labels per sheet' },
  { value: 'LETTER:10', title: 'US Letter - Size: 3.0625" x 1.8375", 10 labels per sheet' },
  { value: 'LETTER:8', title: 'US Letter - Size: 4" x 2.5", 8 labels per sheet' },
  { value: 'LETTER:16', title: 'US Letter - Size: 1.9" x 2.5", 16 labels per sheet' },
  { value: 'A4:40', title: 'A4 - Size: 45.7mm x 25.5mm, 40 labels per sheet' },
  { value: 'A4:18', title: 'A4 - Size: 63.5mm x 46.6mm, 18 labels per sheet' },
  { value: 'A4:10', title: 'A4 - Size: 99.1mm x 57.3mm, 10 labels per sheet' },
  { value: 'A4:8', title: 'A4 - Size: 99.1mm x 67.7mm, 8 labels per sheet' },
  { value: 'A4:20', title: 'A4 - Size: 40mm x 52mm, 20 labels per sheet' },
]

export default function QrLabelContent({
  color,
  size,
  type,
  onChangeColor,
  isProcessing,
  onSizeChange,
  onDownload,
  onCancel,
  onFileFormatChange,
}: {
  color: string
  size: { value: string; title: string }
  type: string
  isProcessing?: boolean
  onChangeColor?: (color: string) => void
  onSizeChange?: (size: { value: string; title: string }) => void
  onDownload?: () => void
  onCancel?: () => void
  onFileFormatChange?: (format: QrCodeFileFormat) => void
}) {
  useEffect(() => {
    if (type === 'label') {
      onSizeChange?.(options[0])
      onFileFormatChange?.('PDF' as QrCodeFileFormat)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type])

  return (
    <div className={css.wrapper}>
      <p className={`${css.title} text-grey-1000-70`}>
        Automatically generate a printable file of the QR Codes in PDF
      </p>
      <div style={{ minHeight: '230px' }} className="flex mb-5">
        <div className={css.col}>
          <section>
            <span className="block mb-1 text-grey-1000-70">Label Layout</span>
            <PrintOptionsComponent
              items={options}
              popoverProps={{ minimal: true }}
              itemRenderer={(i, { handleClick }) => (
                <MenuItem key={i.value} text={i.title} onClick={handleClick} />
              )}
              filterable={false}
              onItemSelect={(i) => onSizeChange?.(i)}>
              <Button text={size.title} rightIconName="chevron-down" />
            </PrintOptionsComponent>
          </section>
          <section>
            <label>
              <span className="text-grey-1000">Select QR Code color</span>
              <SimpleColorPicker
                colors={colors}
                onChange={(val) => onChangeColor?.(val)}
                value={color}
                buttonStyle={{ padding: 6 }}
                containerStyle={{ marginLeft: 100 }}
              />
            </label>
          </section>
        </div>
      </div>
      <section style={{ paddingRight: 20 }} className="flex justify-end">
        <Button text="Cancel" onClick={() => onCancel?.()} />
        <Button
          style={{ marginLeft: 18 }}
          intent={Intent.PRIMARY}
          blue={true}
          text="Download"
          disabled={isProcessing}
          onClick={() => onDownload?.()}
        />
      </section>
    </div>
  )
}
